import { useCallback, type MouseEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import {
    Body1,
    Box,
    Button,
    Flex,
    IconChevronRight,
    Image,
    Modal,
    Stack,
    Subtitle2,
    TextButton,
} from '@myphx-engineering/phx-design-system';
import { useDisclosure } from '@myphx-engineering/phx-design-system/hooks';

import type {
    NdcSwitchTargetV2,
    SwitchRequestSource,
} from '../../../../types/drug-config';
import { ConditionalComponent } from '../../../utility/ConditionalComponent';

export const DrugCardInfo = ({
    info,
    iconBgColor = 'white',
}: {
    info: SwitchRequestSource | NdcSwitchTargetV2;
    iconBgColor?: 'primary' | 'white';
}) => {
    const { t } = useTranslation();
    const [showInfoModal, { toggle, close }] = useDisclosure(false);

    const hasModal = !!info.learnMoreModal;

    const iconBg =
        iconBgColor === 'white' ? 'white' : `var(--mantine-color-primary-0)`;

    const handleToggle = useCallback(
        (e: MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            toggle();
        },
        [toggle]
    );

    return (
        <Box>
            <Flex gap="sm" align="center">
                {info.iconUrl ? (
                    <Flex
                        h="3rem"
                        w="3rem"
                        align="center"
                        justify="center"
                        bg={iconBg}
                        style={{ borderRadius: '0.5rem' }}
                    >
                        <Image
                            src={info.iconUrl}
                            aria-hidden="true"
                            fit="contain"
                            w="auto"
                        />
                    </Flex>
                ) : null}
                <Stack gap="xxs">
                    <Subtitle2>{info.description}</Subtitle2>
                    <ConditionalComponent condition={hasModal}>
                        <TextButton
                            variant="subtle"
                            onClick={handleToggle}
                            justify="start"
                            size="sm"
                            RightIcon={IconChevronRight}
                        >
                            {'Learn more'}
                        </TextButton>
                    </ConditionalComponent>
                </Stack>
            </Flex>
            <ConditionalComponent condition={hasModal}>
                <Box onClick={(e) => e.stopPropagation()}>
                    <Modal
                        opened={showInfoModal}
                        onClose={close}
                        title={info.learnMoreModal?.title}
                    >
                        <Stack>
                            <Stack gap="xs">
                                {info.learnMoreModal?.description.map(
                                    (line) => (
                                        <Body1 key={line}>
                                            <Trans
                                                ns="dynamic"
                                                components={{
                                                    b: <strong />,
                                                    br: <br />,
                                                }}
                                            >
                                                {line}
                                            </Trans>
                                        </Body1>
                                    )
                                )}
                            </Stack>
                            <Button onClick={close}>{t('common.close')}</Button>
                        </Stack>
                    </Modal>
                </Box>
            </ConditionalComponent>
        </Box>
    );
};
